import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useAutoBackOnManagingChange from 'hooks/useAutoBackOnManagingChange';
import { useQuery } from 'hooks/useCustomQuery';
import Button from '../../../components/v4/Button';
import Heading from '../../../components/v4/Heading';
import AddressFields from './AddressFields';
import AddressCustomAttributes from './AddressCustomAttributes';
import useAddress from '../../../hooks/useAddress';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Checkbox,
    VStack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import { axios } from '../../../services/axios';
import { useSelector } from 'react-redux';
import Alert from '../../../components/v4/Alert';
import AlertDescription from '../../../components/v4/AlertDescription';
import AlertIcon from '../../../components/v4/AlertIcon';
import Text from '../../../components/v4/Text';
import { ConfirmationDialog } from '@/components/v4';
import deleteAddress from '../../../API/Addresses/deleteAddress';

import walkthroughIds from '../walkthroughIds';
import { toast } from 'react-toastify';
import { Card } from 'components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { Link } from 'react-router-dom';
import {
    customTabStylesCombineTheme,
    selectedTabStylesCombineTheme,
} from '@/constants';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
export default function AddressEdit({ setAddressAction, rowData }) {
    const dispatch = useDispatch();
    const { addressFields, setAddressField } = useAddress(rowData);
    const { data: company } = useGetCurrentCompany();
    const companyName = company?.name;
    const companyID = company?.id;
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);
    const { addresses } = walkthroughIds;

    const [selectedPlatforms, setSelectedPlatforms] = useState([]);

    useAutoBackOnManagingChange({
        enabled: true,
        onBack: () => setAddressAction('Get'),
    });
    const [existingPlatformIDs, setExistingPlatformIDs] = useState([]);
    const [isAddressLocked, setIsAddressLocked] = useState(false);
    const [showValidationError, setShowValidationError] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Extract existing platform IDs from platformAddresses array
    useEffect(() => {
        if (
            rowData?.platformAddresses &&
            Array.isArray(rowData.platformAddresses)
        ) {
            const existingIDs = rowData.platformAddresses.map(
                (pa) => pa.platformID,
            );
            setExistingPlatformIDs(existingIDs);
            setSelectedPlatforms(existingIDs);
            // Lock fields if there are any platform addresses
            setIsAddressLocked(existingIDs.length > 0);
        }
    }, [rowData]);
    const { data: countriesRaw = [] } = useQuery({
        queryKey: ['countriesList'],
        queryFn: async () => {
            const res = await axios.get('/countries/list');
            return res.data;
        },
    });
    const countries = countriesRaw;

    const { data: availablePlatforms = [], isFetching: platformsLoading } =
        useQuery({
            queryKey: ['companyPlatforms', companyID],
            queryFn: async () => {
                const res = await axios.get(
                    `/Company/${companyID}/Platforms/Available`,
                    { params: { AddressManagedOnly: true } },
                );
                return res.data || [];
            },
            enabled: Boolean(companyID),
        });

    async function onAddressEdit(e) {
        e.preventDefault();

        // Validate required field
        if (
            !addressFields.streetHouseNumber1 ||
            (typeof addressFields.streetHouseNumber1 === 'string' &&
                addressFields.streetHouseNumber1.trim() === '')
        ) {
            toast.error('Street House Number 1 is required');
            setShowValidationError(true);
            return;
        }

        // Clear validation error if field is filled
        setShowValidationError(false);

        setIsSaving(true);

        let compName;
        if (
            addressFields.companyName === undefined ||
            addressFields.companyName === null
        ) {
            compName = companyName;
        } else {
            compName = addressFields.companyName;
        }

        // Convert number fields to actual numbers
        const numberFields = [
            'building1stNumber',
            'buildingFloorNumber',
            'building2ndNumber',
            'streetHouseNumber1',
            'streetHouseNumber2',
        ];

        const processedFields = { ...addressFields };
        numberFields.forEach((field) => {
            if (processedFields[field] && processedFields[field] !== '') {
                const numValue = Number(processedFields[field]);
                if (!isNaN(numValue)) {
                    processedFields[field] = numValue;
                }
            }
        });

        const addressFieldNames = {
            buildingFloorNumber: 'Building Floor Number',
            building1stNumber: 'Building 1st Number',
            building2ndNumber: 'Building 2nd Number',
            streetHouseNumber1: 'Street House Number 1',
            streetHouseNumber2: 'Street House Number 2',
            streetName: 'Street Name',
            suburb: 'Suburb',
            city: 'City',
            state: 'State',
            postCode: 'Post Code',
            country: 'Country',
            companyName: 'Company Name',
        };

        const getAddressErrorMessage = (error) => {
            const data = error?.response?.data;
            if (data?.errors && typeof data.errors === 'object') {
                const messages = Object.entries(data.errors)
                    .map(([field, msgs]) => {
                        const label = addressFieldNames[field] || field;
                        const msg = Array.isArray(msgs) ? msgs[0] : msgs;
                        return `${label}: ${msg}`;
                    })
                    .join('\n');
                if (messages) return messages;
            }
            return (
                data?.title ||
                data?.message ||
                'There was an error editing this address.'
            );
        };

        try {
            // Only update address fields if not locked by a platform
            if (!isAddressLocked) {
                await axios.put('/address', {
                    companyID: companyID,
                    companyName: compName,
                    ...processedFields,
                });
            }

            // Create platform addresses for newly selected platforms (not already existing)
            const newPlatforms = selectedPlatforms.filter(
                (id) => !existingPlatformIDs.includes(id),
            );
            if (newPlatforms.length > 0) {
                try {
                    for (const platformId of newPlatforms) {
                        await axios.post(
                            `/Address/${addressFields.id}/Platform/${platformId}/Add`,
                        );
                    }
                    setIsAddressLocked(true);
                    toast.success(
                        `Address linked to ${newPlatforms.length} new platform(s) successfully`,
                    );
                } catch (platformError) {
                    toast.error('Failed to link address to platform');
                }
            } else {
                toast.success('Address updated successfully');
            }

            dispatch({
                type: 'CHANGE_TABLE_LOADING',
            });
            setAddressAction('Get');
        } catch (error) {
            toast.error(getAddressErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    }
    async function handleDeleteConfirm() {
        setIsDeleting(true);
        try {
            await deleteAddress(rowData.id);
            toast.success('Address deleted successfully');
            setAddressAction('Get');
        } catch {
            toast.error('Failed to delete address');
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    }

    const breadCrumbTrail = [
        { label: 'Address', path: ['', { index: 0 }] },
        { label: 'Edit Address', path: '' },
    ];
    return (
        <>
            <>
                <Box as="form" onSubmit={onAddressEdit}>
                    <Breadcrumb sx={breadCrumbStyling}>
                        {breadCrumbTrail?.map((crumb, index) => (
                            <BreadcrumbItem key={index}>
                                {Array.isArray(crumb.path) ? (
                                    <BreadcrumbLink
                                        as={Link}
                                        to={crumb.path[0]}
                                        state={crumb.path[1]}
                                        onClick={
                                            index === 0
                                                ? () => setAddressAction('Get')
                                                : undefined
                                        }>
                                        {crumb.label}
                                    </BreadcrumbLink>
                                ) : (
                                    <BreadcrumbLink
                                        as={Link}
                                        to={crumb.path}
                                        onClick={
                                            index === 0
                                                ? () => setAddressAction('Get')
                                                : undefined
                                        }>
                                        {crumb.label}
                                    </BreadcrumbLink>
                                )}
                            </BreadcrumbItem>
                        ))}
                    </Breadcrumb>

                    <Tabs isLazy index={selectedTab} onChange={setSelectedTab}>
                        <TabList borderBottom="none">
                            <Tab
                                sx={{
                                    ...customTabStylesCombineTheme,
                                    color: darkMode
                                        ? 'rgba(255, 255, 255)'
                                        : 'inherit',
                                }}
                                _selected={{
                                    ...selectedTabStylesCombineTheme,
                                    textColor: darkMode
                                        ? 'rgba(255, 255, 255)'
                                        : 'inherit',
                                }}>
                                Details
                            </Tab>
                            {requiredScope >= 60 && (
                                <Tab
                                    sx={{
                                        ...customTabStylesCombineTheme,
                                        color: darkMode
                                            ? 'rgba(255, 255, 255)'
                                            : 'inherit',
                                    }}
                                    _selected={{
                                        ...selectedTabStylesCombineTheme,
                                        textColor: darkMode
                                            ? 'rgba(255, 255, 255)'
                                            : 'inherit',
                                    }}>
                                    Custom
                                </Tab>
                            )}
                        </TabList>
                        <TabPanels padding={0}>
                            <TabPanel p={0}>
                                {existingPlatformIDs.length > 0 && (
                                    <Box
                                        bg={darkMode ? '#5A4A1F' : '#FEF3CD'}
                                        p={4}
                                        borderRadius={8}
                                        maxWidth={895}
                                        mt={4}
                                        mb={4}>
                                        <Text
                                            fontWeight="bold"
                                            color={
                                                darkMode ? '#FFE69C' : '#7A5C0F'
                                            }
                                            mb={1}>
                                            Cannot Edit Address
                                        </Text>
                                        <Text
                                            fontSize="sm"
                                            color={
                                                darkMode ? '#FFE69C' : '#7A5C0F'
                                            }>
                                            This address is synced with one or
                                            more external platforms and its
                                            details cannot be changed.
                                        </Text>
                                    </Box>
                                )}

                                <Card
                                    borderRadius={15}
                                    padding="0px 30px"
                                    maxWidth={895}
                                    ml={0}
                                    mt={existingPlatformIDs.length > 0 ? 0 : 4}
                                    bg={darkMode ? 'dark.tcap' : null}>
                                    <AddressFields
                                        fields={addressFields}
                                        fieldHandler={setAddressField}
                                        countries={countries}
                                        isLocked={isAddressLocked}
                                        showStreetHouseNumber1Error={
                                            showValidationError
                                        }
                                    />
                                </Card>

                                {/* Platform Address Section - Show if platforms available */}
                                {!platformsLoading &&
                                    availablePlatforms?.length > 0 && (
                                        <>
                                            <Box
                                                sx={{
                                                    marginBottom: '25px',
                                                }}></Box>
                                            <Card
                                                borderRadius={15}
                                                padding="0px 30px"
                                                maxWidth={895}
                                                ml={0}
                                                bg={
                                                    darkMode
                                                        ? 'dark.tcap'
                                                        : null
                                                }>
                                                <Heading
                                                    size="md"
                                                    mt={4}
                                                    mb={4}>
                                                    {existingPlatformIDs.length >
                                                    0
                                                        ? 'Platform Addresses'
                                                        : 'Create Platform Addresses'}
                                                </Heading>
                                                <Text fontSize="sm" mb={4}>
                                                    {existingPlatformIDs.length >
                                                    0
                                                        ? 'This address is already linked to the checked platform(s). You can link to additional platforms.'
                                                        : 'Link this address to platforms for integrated management.'}
                                                </Text>
                                                <VStack
                                                    align="stretch"
                                                    spacing={2}
                                                    mb={4}>
                                                    {availablePlatforms?.map(
                                                        (platform) => {
                                                            const isExisting =
                                                                existingPlatformIDs.includes(
                                                                    platform.id,
                                                                );
                                                            return (
                                                                <Checkbox
                                                                    key={
                                                                        platform.id
                                                                    }
                                                                    isChecked={selectedPlatforms.includes(
                                                                        platform.id,
                                                                    )}
                                                                    isDisabled={
                                                                        isExisting
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        if (
                                                                            e
                                                                                .target
                                                                                .checked
                                                                        ) {
                                                                            setSelectedPlatforms(
                                                                                [
                                                                                    ...selectedPlatforms,
                                                                                    platform.id,
                                                                                ],
                                                                            );
                                                                        } else {
                                                                            setSelectedPlatforms(
                                                                                selectedPlatforms.filter(
                                                                                    (
                                                                                        id,
                                                                                    ) =>
                                                                                        id !==
                                                                                        platform.id,
                                                                                ),
                                                                            );
                                                                        }
                                                                    }}>
                                                                    {platform.name ===
                                                                    'Webex'
                                                                        ? 'WebEx Local Gateway'
                                                                        : platform.name}{' '}
                                                                    {isExisting && (
                                                                        <Text
                                                                            as="span"
                                                                            fontSize="xs"
                                                                            color="gray.500">
                                                                            (Already
                                                                            linked)
                                                                        </Text>
                                                                    )}
                                                                </Checkbox>
                                                            );
                                                        },
                                                    )}
                                                </VStack>
                                                {selectedPlatforms.filter(
                                                    (id) =>
                                                        !existingPlatformIDs.includes(
                                                            id,
                                                        ),
                                                ).length > 0 && (
                                                    <Box paddingBottom={5}>
                                                        <Alert
                                                            status="warning"
                                                            mt={3}>
                                                            <AlertIcon />
                                                            <AlertDescription>
                                                                <Text fontSize="sm">
                                                                    Once this
                                                                    address is
                                                                    saved with a
                                                                    platform
                                                                    link, the
                                                                    address
                                                                    fields will
                                                                    become
                                                                    read-only
                                                                    and cannot
                                                                    be edited.
                                                                </Text>
                                                            </AlertDescription>
                                                        </Alert>
                                                    </Box>
                                                )}
                                            </Card>
                                        </>
                                    )}
                            </TabPanel>
                            {requiredScope >= 60 && (
                                <TabPanel p={0}>
                                    <AddressCustomAttributes
                                        darkMode={darkMode}
                                        addressFields={addressFields}
                                        setAddressField={setAddressField}
                                    />
                                </TabPanel>
                            )}
                        </TabPanels>
                    </Tabs>

                    <Box sx={{ marginBottom: '25px' }}></Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            maxWidth: 895,
                        }}>
                        <div
                            style={{
                                border: 'none',
                                textAlign: 'center',
                                justifyContent: 'center',
                            }}>
                            <Button
                                type="submit"
                                data-walkthroughid={addresses.ufSaveButton}
                                className="h-10 min-w-[5rem] font-semibold"
                                isLoading={isSaving}
                                loadingText="Saving"
                                isDisabled={isSaving}
                                sx={{ width: 142 }}>
                                Save
                            </Button>
                            <div
                                style={{
                                    marginLeft: 30,
                                    display: 'inline',
                                }}>
                                <Button
                                    className="secondary h-10 min-w-[5rem] font-semibold"
                                    variant="outline"
                                    background="white"
                                    sx={{ width: 142 }}
                                    data-walkthroughid={addresses.ufBackButton}
                                    onClick={() => setAddressAction('Get')}>
                                    Back
                                </Button>
                            </div>
                        </div>
                        {false && existingPlatformIDs.length === 0 && (
                            <Button
                                colorScheme="red"
                                sx={{ width: 142 }}
                                onClick={() => setShowDeleteDialog(true)}>
                                Delete
                            </Button>
                        )}
                    </Box>
                </Box>
            </>
            <ConfirmationDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Address"
                message={`Are you sure you want to delete "${rowData?.description || 'this address'}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmColorScheme="red"
                isLoading={isDeleting}
            />
        </>
    );
}
